import type { Temporal } from '@js-temporal/polyfill'
import type { ComputedRef, Ref } from 'vue'
import { computed } from 'vue'
import { TIME_LINE_HOUR_WIDTH } from '@/src/lib/constants'

// タイムライン全体の高さ（resources.$app-timeline-height）
const TOTAL_TIMELINE_HEIGHT = 120

// 上下の余白（均等に配分）
const VERTICAL_PADDING = 20

// 予定バー領域の高さ（上下の余白を除いた高さ）
const TIMELINE_HEIGHT = TOTAL_TIMELINE_HEIGHT - VERTICAL_PADDING * 2 // 120 - 40 = 80px

// 返り値の型定義: left/widthは必須、top/heightはオプショナル
type ScheduleBarStyle = {
  left: string
  width: string
  top?: string
  height?: string
}

// 引数の型定義: laneとtotalLanesは両方指定か両方未指定
type UseScheduleBarStyleArgs =
  | {
      from: Ref<Temporal.PlainTime>
      to: Ref<Temporal.PlainTime>
    }
  | {
      from: Ref<Temporal.PlainTime>
      to: Ref<Temporal.PlainTime>
      lane: Ref<number | undefined>
      totalLanes: Ref<number | undefined>
    }

export function useScheduleBarStyle(args: UseScheduleBarStyleArgs): ComputedRef<ScheduleBarStyle> {
  const { from, to } = args
  const lane = 'lane' in args ? args.lane : undefined
  const totalLanes = 'totalLanes' in args ? args.totalLanes : undefined
  const barStart = computed(() => from.value.since('00:00').total({ unit: 'hours' }) * TIME_LINE_HOUR_WIDTH)

  const barWidth = computed(() => {
    return from.value.until(to.value).total({ unit: 'hours' }) * TIME_LINE_HOUR_WIDTH
  })

  // 各レーンの高さを計算（タイムライン全体の高さ ÷ 総レーン数）
  const laneHeight = computed(() => {
    if (totalLanes?.value && totalLanes.value > 0) {
      return TIMELINE_HEIGHT / totalLanes.value
    }
    return TIMELINE_HEIGHT
  })

  const barTop = computed(() => {
    // 上部の余白を基準位置とする
    let top = VERTICAL_PADDING

    if (lane?.value !== undefined && totalLanes?.value !== undefined) {
      // レーンの基本位置を計算
      top += lane.value * laneHeight.value
    }

    return top
  })

  const style = computed<ScheduleBarStyle>(() => {
    const baseStyle: ScheduleBarStyle = {
      left: `${barStart.value + 1}px`,
      width: `${barWidth.value - 2}px`,
    }

    // lane/totalLanesが指定されている場合のみtop/heightを追加（後方互換性のため）
    if (lane !== undefined && totalLanes !== undefined) {
      return {
        ...baseStyle,
        top: `${barTop.value}px`,
        height: `${laneHeight.value}px`,
      }
    }

    return baseStyle
  })

  return style
}
